import { useEffect, useState } from "react";
import { Col, Container, Form } from "react-bootstrap";
import { COLORS } from "utils/colors";

import moment from "moment-timezone";
import DateObject from "react-date-object";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";

/**
 * DatePickerEdit component wraps a multi-date picker input with custom styling and logic.
 * It allows selecting dates with visual highlighting for date ranges and supports custom input rendering.
 * @memberof module:components/inputs
 * @param {Object} props
 * @param {string} props.label - Label text displayed above the date picker.
 * @param {string|Date|Array} props.value - The controlled value of the date picker.
 * @param {string|Date} props.startDate - The initial date to show in the picker.
 * @param {Array<{startDate: string, stopDate: string}>} props.dates - Array of date ranges to highlight in the calendar. Dates are strings formatted as "DD-MM-YYYY".
 * @param {string} [props.type] - Optional input type, unused currently.
 * @param {boolean} [props.disabled] - If true, disables the date picker input.
 * @param {string} [props.placeholder] - Placeholder text for the input.
 * @param {string} [props.error] - Error message or flag that styles the input red if truthy.
 * @param {React.ReactNode} [props.children] - Optional children to render inside the component.
 * @param {Function} [props.onFocus] - Callback fired when the input receives focus.
 * @param {Function} [props.onChange] - Callback fired when the date selection changes. Receives the new date value.
 * @param {Function} [props.onKeyDown] - Callback fired on key down event inside the input.
 *
 * @returns {JSX.Element} Rendered date picker component.
 */
const DateTimePickerEdit = ({
  label,
  value,
  startDate,
  dates,
  type,
  disabled,
  placeholder,
  error,
  children,
  onFocus,
  onChange,
  onKeyDown,
}) => {
  const [isFocused, setIsFocus] = useState(false);
  const [valueEdit, setValue] = useState(value);
  const [errorEdit, setError] = useState(error);

  /**
   * Custom input component for the DatePicker.
   * Displays a calendar icon that changes when focused and overlays a transparent input field.
   *
   * @param {Object} inputProps
   * @param {Function} inputProps.onFocus - Focus event handler.
   * @param {string} inputProps.value - Input value.
   * @param {Function} inputProps.onChange - Change event handler.
   * @param {Function} inputProps.onBlur - Blur event handler.
   * @param {boolean} inputProps.disabled - Whether the input is disabled.
   */
  const CustomInput = ({ onFocus, value, onChange, onBlur, disabled }) => {
    return (
      <Container className="position-relative p-0 d-flex align-items-center">
        {/* Image Element */}
        <img
          style={{
            position: "relative",
            marginLeft: "auto",
            // width: "100%", // Optional: adjust width as needed
            color: "red",
          }}
          width={16}
          height={16}
          src={
            isFocused ? "/icons/calendar-focused.png" : "/icons/calendar.png"
          }
          alt={"Arrow Icon"}
        />

        {/* Input Element */}
        <Form.Control
          type="text"
          className="position-absolute top-50 start-50 translate-middle"
          style={{
            width: "100%",
            color: isFocused
              ? COLORS.selectedColorTheme
              : error
                ? "red"
                : COLORS.greyFontColor,
            margin: 0,
            background: "transparent",
            border: 0,
          }}
          disabled={disabled}
          value={value}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
        />
      </Container>
    );
  };

  useEffect(() => {
    setValue(value);
  }, [value]);

  useEffect(() => {
    setError(error);
  }, [error]);

  return (
    <>
      <Form.Label
        column
        style={{
          fontWeight: isFocused ? "bold" : "normal",
          color: isFocused
            ? COLORS.selectedColorTheme
            : error
              ? "red"
              : COLORS.greyFontColor,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {label}
      </Form.Label>
      <Col>
        <DatePicker
          key={"change-date-btn"}
          value={new DateObject(startDate).toUTC()}
          containerStyle={{
            width: "100%",
            background: "#1f1f1f",
            padding: "0.6rem 0.375rem",
            opacity: disabled ? 0.5 : 1,
            border: isFocused
              ? "2px solid " + COLORS.selectedColorTheme
              : error
                ? "2px solid red"
                : "2px solid " + COLORS.greyFontColor,
            borderRadius: "0.375rem",
          }}
          disabled={disabled}
          plugins={[<TimePicker />]}
          // locale="utc"
          format="DD/MM/YYYY HH:mm:ss"
          render={
            <CustomInput disabled={disabled}  />
          }
          onChange={(e) => {
            // e is the picked value (DateObject)
            const offsetMinutes = new Date(e).getTimezoneOffset(); // e.g., +2 hours
            const pickedDate = new DateObject(e).toDate(); // convert to JS Date in UTC

            // Add offset
            const adjustedDate = new Date(
              pickedDate.getTime() - offsetMinutes * 60 * 1000,
            );

            // Call parent onChange with ISO string
            onChange && !disabled && onChange(adjustedDate.toISOString());
            
          }}
          onOpen={(e) => {
            setIsFocus(true);
          }}
          onClose={(e) => {
            setIsFocus(false);
          }}
          // mapDays={({
          //   date,
          //   today,
          //   selectedDate,
          //   currentMonth,
          //   isSameDate,
          // }) => {
          //   if (!date) return;
          //   var dateWthTime = date.toDate();
          //   dateWthTime.setHours(0, 0, 0, 0);
          //   let props = {};

          //   if (isSameDate(date, selectedDate)) {
          //     props.style = {
          //       color: "#fff",
          //       backgroundColor: "#0275d8",
          //       fontWeight: "bold",
          //       borderRadius: "0",
          //     };

          //     return props;
          //   }

          //   for (var index = 0; index < dates?.length; index++) {
          //     var element = dates[index];

          //     if (
          //       moment(element.startDate, "DD-MM-YYYY").toDate().getTime() ===
          //       dateWthTime.getTime()
          //     ) {
          //       props.style = {
          //         color: "#fff",
          //         backgroundColor: "rgba(92, 184, 92, 0.9)",
          //         fontWeight: "bold",
          //         borderTopLeftRadius: "50%",
          //         borderTopRightRadius: "0",
          //         borderBottomLeftRadius: "50%",
          //         borderBottomRightRadius: "0",
          //         left: 1,
          //         right: 0,
          //       };

          //       return props;
          //     }

          //     if (
          //       moment(element.stopDate, "DD-MM-YYYY").toDate().getTime() ===
          //       dateWthTime.getTime()
          //     ) {
          //       props.style = {
          //         color: "#fff",
          //         backgroundColor: "rgba(92, 184, 92, 0.9)",
          //         fontWeight: "bold",
          //         borderTopLeftRadius: "0",
          //         borderTopRightRadius: "50%",
          //         borderBottomLeftRadius: "0",
          //         borderBottomRightRadius: "50%",
          //         left: 0,
          //         right: 1,
          //       };
          //       return props;
          //     }

          //     if (
          //       moment(element.startDate, "DD-MM-YYYY").toDate() <
          //         date.toDate() &&
          //       date.toDate() < moment(element.stopDate, "DD-MM-YYYY").toDate()
          //     ) {
          //       props.style = {
          //         color: "#fff",
          //         backgroundColor: "rgba(92, 184, 92, 0.9)",
          //         fontWeight: "bold",
          //         borderRadius: "0",
          //         left: 0,
          //         right: 0,
          //       };
          //       return props;
          //     }
          //   }
          // }}
        />
      </Col>
    </>
  );
};

export default DateTimePickerEdit;
